/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo } from "react";
import { getFirestore, collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { useEffect, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";


// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Doughnut } from "react-chartjs-2";

// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadgeDot from "components/SoftBadgeDot";

// DefaultDoughnutChart configurations
import configs from "examples/Charts/DoughnutCharts/DefaultDoughnutChart/configs";

function DefaultDoughnutChart({ title, description, height, chart, audioUsage }) {
  const [maxHours, setMaxHours] = useState(100); // Default maxHours
  const [currentUserSubscription, setCurrentUserSubscription] = useState(null);

  // Other component setup...

  useEffect(() => {
    const auth = getAuth();
    onAuthStateChanged(auth, (user) => {
      if (user) {
        const fetchUserSubscription = async () => {
          const db = getFirestore();
          const userSubDoc = collection(db, 'customerDetails', user.uid, 'subscriptions');
          const docSnap = await getDocs(userSubDoc);

          let latestSubscription = null;
  
          docSnap.forEach((doc) => {
            const subscriptionData = doc.data();
            // Assuming there's a 'startDate' or similar timestamp field to determine the most recent subscription
            if (!latestSubscription || subscriptionData.current_period_start.toDate() > latestSubscription.current_period_start.toDate()) {
              if (["active", "trialing"].includes(subscriptionData.status)) {
                latestSubscription = subscriptionData;
              }
            }
          });

          const planHoursMapping = {
            "price_1P3kdWLiDfhhXJlFnroutVz5": 70,
            "price_1P3kgVLiDfhhXJlFyAPLbcjy": 120,
            "price_1P5UcILiDfhhXJlFkZDmU8VZ": 250,
            "price_1PIu0ZLiDfhhXJlFNePzCIzm": 250,
            // Add more plan IDs and their corresponding hours here
          };

          if (latestSubscription) {
            const planId = latestSubscription.items[0].plan.id; // Adjust based on your data structure
            const hours = planHoursMapping[planId] || 0; 
            setMaxHours(hours); // Set maxHours based on the plan
            console.log("hours: ", hours)
            setCurrentUserSubscription(latestSubscription);
          }
        };

        fetchUserSubscription();
      }
    });
  }, []);

  const usedHours = Number((audioUsage / 3600).toFixed(2));
  const remainingHours = Math.max(maxHours - usedHours, 0);
  const remainingHoursFixed = Number(remainingHours.toFixed(2));

  const chartData = {
    labels: ["Used Hours", "Remaining Hours"],
    datasets: [{
      label: "Audio Usage",
      backgroundColors: ["light", "info"], 
      data: [usedHours, remainingHoursFixed]
    }]
  };

  function renderBadgeDots() {
    if (!chartData.datasets?.[0]?.backgroundColors) {
      return null; // or some default UI element
    }
  
    return chartData.labels.map((label, index) => (
      <SoftBadgeDot
        key={`badge-dot-${index}`}
        color={chartData.datasets[0].backgroundColors[index]}
        badgeContent={label}
      />
    ));
  }
  

  const { data, options } = configs(chartData.labels || [], chartData.datasets[0] || {}, chart.cutout);

  const renderChart = (
    <SoftBox p={2} mb={2.5}>
      {title || description ? (
        <SoftBox px={description ? 1 : 0} pt={description ? 1 : 0}>
          {title && (
            <SoftBox mb={1}>
              <SoftTypography variant="h5">{title}</SoftTypography>
            </SoftBox>
          )}
          <SoftBox mb={2}>
            <SoftTypography component="div" variant="button" fontWeight="regular" color="text">
              {description}
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      ) : null}
      {useMemo(
        () => (
          <SoftBox height={height}>
            <Doughnut data={data} options={options} />
          </SoftBox>
        ),
        [audioUsage, height]
      )}
      {/*<SoftBox position="absolute" top="20px" right="20px">
          <SoftTypography variant="button" fontWeight="medium" color="dark">
            Remaining Hours: {remainingHours.toFixed(2)}
          </SoftTypography>
        </SoftBox>*/}
    </SoftBox>
  );

  return title || description ? <Card>{renderChart}</Card> : renderChart;
}

// Setting default values for the props of DefaultDoughnutChart
DefaultDoughnutChart.defaultProps = {
  title: "",
  description: "",
  height: "19.125rem",
};

// Typechecking props for the DefaultDoughnutChart
DefaultDoughnutChart.propTypes = {
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chart: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.object])).isRequired,
  audioUsage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default DefaultDoughnutChart;
